import { useState, useEffect, useCallback, useRef } from 'react';
import { useConversation } from '@elevenlabs/react';
import { useAuth } from '../contexts/AuthContext';

interface LanguageOption {
  code: string;
  name: string;
  flag: string;
}

// Define talk time limits for each commitment tier (in seconds)
const TALK_TIME_LIMITS: Record<string, number> = {
  'seeker': 3 * 60, // 3 minutes per day
  'awakening': 15 * 60, // 15 minutes per day
  'aligned': 30 * 60, // 30 minutes per day
  'abundant': Infinity, // Unlimited
};

export const useElevenLabsAI = (
  selectedLanguage?: LanguageOption,
  onManifestationGenerated?: (manifestation: string) => void
) => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [userTranscript, setUserTranscript] = useState<string>('');
  const [aiTranscript, setAiTranscript] = useState<string>('');
  const [extractedManifestation, setExtractedManifestation] = useState<string>('');
  const [pendingManifestation, setPendingManifestation] = useState<string>('');
  const [agreementDetected, setAgreementDetected] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Talk time tracking
  const [dailyTalkTimeUsed, setDailyTalkTimeUsed] = useState<number>(0);
  const [lastTalkDate, setLastTalkDate] = useState<string>('');
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [talkTimeWarning, setTalkTimeWarning] = useState<boolean>(false);
  
  // Use useRef to maintain manifestation state across re-renders
  const currentManifestationRef = useRef<string>('');
  const manifestationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationStartTimeRef = useRef<number | null>(null);
  const talkTimeLimitRef = useRef<number>(TALK_TIME_LIMITS['seeker']);
  const talkTimeIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 3;

  // Get environment variables
  const apiKey = import.meta.env.VITE_ELEVENLABS_API_KEY;
  const agentId = import.meta.env.VITE_ELEVENLABS_AGENT_ID;

  // Validate API credentials
  const validateCredentials = useCallback(() => {
    if (!apiKey || apiKey === 'your_elevenlabs_api_key_here' || apiKey.length < 10) {
      return 'Invalid or missing ElevenLabs API key. Please check your .env file.';
    }
    
    if (!agentId || agentId === 'your_elevenlabs_agent_id_here' || !agentId.startsWith('agent_')) {
      return 'Invalid or missing ElevenLabs Agent ID. Please check your .env file.';
    }

    return null;
  }, [apiKey, agentId]);

  // Initialize talk time tracking
  useEffect(() => {
    // Get user's commitment tier
    const userProfile = (user as any)?.profile;
    const commitmentTier = userProfile?.commitment_tier || 'seeker';
    
    // Set talk time limit based on commitment tier
    talkTimeLimitRef.current = TALK_TIME_LIMITS[commitmentTier] || TALK_TIME_LIMITS['seeker'];
    
    // Load talk time from localStorage
    try {
      const today = new Date().toISOString().split('T')[0];
      const storedLastTalkDate = localStorage.getItem('manivo_last_talk_date');
      const storedTalkTime = localStorage.getItem('manivo_daily_talk_time');
      
      // If it's a new day, reset talk time
      if (storedLastTalkDate !== today) {
        setDailyTalkTimeUsed(0);
        setLastTalkDate(today);
        localStorage.setItem('manivo_last_talk_date', today);
        localStorage.setItem('manivo_daily_talk_time', '0');
      } else {
        // Otherwise, load stored talk time
        setDailyTalkTimeUsed(storedTalkTime ? parseInt(storedTalkTime) : 0);
        setLastTalkDate(storedLastTalkDate || today);
      }
      
      // Calculate remaining time
      const remaining = Math.max(0, talkTimeLimitRef.current - (storedTalkTime ? parseInt(storedTalkTime) : 0));
      setRemainingTime(remaining);
    } catch (error) {
      // Reset if there's an error
      setDailyTalkTimeUsed(0);
      setLastTalkDate(new Date().toISOString().split('T')[0]);
    }
  }, [user]);

  // Function to extract manifestation from AI response
  const extractManifestationFromAI = useCallback((text: string) => {
    // Look for "Your manifestation is" pattern
    const manifestationPatterns = [
      /Your manifestation is[:\s]+(.*?)(?:[.!?]|$)/i,
      /Your new manifestation is[:\s]+(.*?)(?:[.!?]|$)/i,
      /Here's your manifestation[:\s]+(.*?)(?:[.!?]|$)/i,
      /The manifestation is[:\s]+(.*?)(?:[.!?]|$)/i,
    ];
    
    for (const pattern of manifestationPatterns) {
      const match = text.match(pattern);
      if (match && match[1] && match[1].trim().length > 5) {
        const manifestation = match[1].trim();
        return manifestation;
      }
    }
    
    return null;
  }, []);

  // Initialize the ElevenLabs conversation hook
  const conversation = useConversation({
    onConnect: () => {
      setIsConnected(true);
      setError(null);
      setIsInitializing(false);
      retryCountRef.current = 0;
      
      // Start tracking conversation time
      conversationStartTimeRef.current = Date.now();
      
      // Start interval to check talk time limit
      startTalkTimeTracking();
    },
    onDisconnect: () => {
      setIsConnected(false);
      
      // Stop tracking and update talk time
      stopTalkTimeTracking();
    },
    onMessage: (message) => {
      // Handle different message types
      if (typeof message === 'object' && message !== null) {
        const messageObj = message as any;
        
        // Handle {source: 'user', message: 'text'} format
        if (messageObj.source === 'user' && messageObj.message) {
          const userText = messageObj.message;
          setUserTranscript(userText.trim());
        }
        
        // Handle AI responses in {source: 'ai', message: 'text'} format
        else if (messageObj.source === 'ai' && messageObj.message) {
          const agentText = messageObj.message;
          setAiTranscript(agentText.trim());
          
          // Check for "Your manifestation is" pattern
          const manifestation = extractManifestationFromAI(agentText);
          if (manifestation) {
            currentManifestationRef.current = manifestation;
            setExtractedManifestation(manifestation);
            
            // Clear any existing timeout
            if (manifestationTimeoutRef.current) {
              clearTimeout(manifestationTimeoutRef.current);
              manifestationTimeoutRef.current = null;
            }
            
            // Trigger manifestation callback
            if (onManifestationGenerated) {
              onManifestationGenerated(manifestation);
            }
          }
        }
        
        // Handle user transcript events
        if (messageObj.type === 'user_transcript' || messageObj.user_transcription_event) {
          const userText = messageObj.user_transcription_event?.user_transcript || 
                          messageObj.user_transcript || 
                          messageObj.transcript;
          
          if (userText) {
            setUserTranscript(userText.trim());
          }
        }
        
        // Handle agent response events
        else if (messageObj.type === 'agent_response' || messageObj.agent_response_event) {
          const agentText = messageObj.agent_response_event?.agent_response || 
                           messageObj.agent_response || 
                           messageObj.response;
          
          if (agentText) {
            setAiTranscript(agentText.trim());
            
            // Check for "Your manifestation is" pattern
            const manifestation = extractManifestationFromAI(agentText);
            if (manifestation) {
              currentManifestationRef.current = manifestation;
              setExtractedManifestation(manifestation);
              
              // Trigger manifestation callback
              if (onManifestationGenerated) {
                onManifestationGenerated(manifestation);
              }
            }
          }
        }
      }
      
      // Legacy handling for simple string messages
      else if (typeof message === 'string') {
        const messageText = String(message);
        setAiTranscript(messageText.trim());
        
        // Check for "Your manifestation is" pattern
        const manifestation = extractManifestationFromAI(messageText);
        if (manifestation) {
          currentManifestationRef.current = manifestation;
          setExtractedManifestation(manifestation);
          
          // Trigger manifestation callback
          if (onManifestationGenerated) {
            onManifestationGenerated(manifestation);
          }
        }
      }
    },
    onStatusChange: (status: string) => {
      // Status updates are handled silently
    },
    onError: (error: any) => {
      // Set a user-friendly error message instead of the technical one
      setError('We\'re having trouble connecting to our manifestation service. Please try again in a moment.');
      setIsConnected(false);
      setIsInitializing(false);
      
      // Stop tracking on error
      stopTalkTimeTracking();
      
      // Auto-retry logic for transient errors
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        setTimeout(() => {
          if (!isConnected) {
            startConversation();
          }
        }, 3000 * retryCountRef.current); // Exponential backoff
      }
    },
  });

  // Start talk time tracking interval
  const startTalkTimeTracking = () => {
    // Clear any existing interval
    if (talkTimeIntervalRef.current) {
      clearInterval(talkTimeIntervalRef.current);
    }
    
    // Start new interval to check talk time limit every second
    talkTimeIntervalRef.current = setInterval(() => {
      if (conversationStartTimeRef.current) {
        const currentDuration = Math.floor((Date.now() - conversationStartTimeRef.current) / 1000);
        const totalUsed = dailyTalkTimeUsed + currentDuration;
        
        // Update remaining time
        const remaining = Math.max(0, talkTimeLimitRef.current - totalUsed);
        setRemainingTime(remaining);
        
        // Show warning when close to limit
        if (remaining <= 30 && !talkTimeWarning && talkTimeLimitRef.current !== Infinity) {
          setTalkTimeWarning(true);
        }
        
        // End conversation if limit reached
        if (totalUsed >= talkTimeLimitRef.current && talkTimeLimitRef.current !== Infinity) {
          stopConversation();
          setError('You have reached your daily talk time limit. Upgrade your commitment to talk more!');
        }
      }
    }, 1000);
  };

  // Stop talk time tracking and update used time
  const stopTalkTimeTracking = () => {
    // Clear interval
    if (talkTimeIntervalRef.current) {
      clearInterval(talkTimeIntervalRef.current);
      talkTimeIntervalRef.current = null;
    }
    
    // Update talk time if conversation was started
    if (conversationStartTimeRef.current) {
      const currentDuration = Math.floor((Date.now() - conversationStartTimeRef.current) / 1000);
      const newTotalUsed = dailyTalkTimeUsed + currentDuration;
      
      // Update state
      setDailyTalkTimeUsed(newTotalUsed);
      
      // Save to localStorage
      localStorage.setItem('manivo_daily_talk_time', newTotalUsed.toString());
      
      // Reset start time
      conversationStartTimeRef.current = null;
      
      // Reset warning flag
      setTalkTimeWarning(false);
    }
  };

  // Check for environment variables on mount
  useEffect(() => {
    const credentialError = validateCredentials();
    if (credentialError) {
      setError('We\'re having trouble connecting to our manifestation service. Please try again in a moment.');
    } else {
      setError(null);
    }
    
    // Set initializing to false after a timeout
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [validateCredentials]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any timeouts and intervals
      if (manifestationTimeoutRef.current) {
        clearTimeout(manifestationTimeoutRef.current);
      }
      if (talkTimeIntervalRef.current) {
        clearInterval(talkTimeIntervalRef.current);
      }
    };
  }, []);

  const startConversation = useCallback(async () => {
    // Validate credentials first
    const credentialError = validateCredentials();
    if (credentialError) {
      setError('We\'re having trouble connecting to our manifestation service. Please try again in a moment.');
      return;
    }

    // Check if user has reached their daily talk time limit
    const userProfile = (user as any)?.profile;
    const commitmentTier = userProfile?.commitment_tier || 'seeker';
    const talkTimeLimit = TALK_TIME_LIMITS[commitmentTier] || TALK_TIME_LIMITS['seeker'];
    
    if (dailyTalkTimeUsed >= talkTimeLimit && talkTimeLimit !== Infinity) {
      setError(`You have reached your daily limit of ${Math.floor(talkTimeLimit / 60)} minutes. Upgrade your commitment to talk more!`);
      return;
    }

    try {
      setIsInitializing(true);
      
      // Reset transcripts and agreement states when starting new conversation
      setUserTranscript('');
      setAiTranscript('');
      setExtractedManifestation('');
      setPendingManifestation('');
      setAgreementDetected(false);
      currentManifestationRef.current = '';
      
      // Clear any existing manifestation timeout
      if (manifestationTimeoutRef.current) {
        clearTimeout(manifestationTimeoutRef.current);
        manifestationTimeoutRef.current = null;
      }
      
      // Request microphone permission
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micError) {
        setError('Please allow microphone access to continue your manifestation journey.');
        setIsInitializing(false);
        return;
      }
      
      // Prepare conversation options with language if provided
      const conversationOptions: any = {
        agentId: agentId,
        apiKey: apiKey,
      };

      // Add language configuration if selected
      if (selectedLanguage && selectedLanguage.code !== 'en') {
        conversationOptions.overrides = {
          agent: {
            language: selectedLanguage.code,
          }
        };
      }
      
      // Start the conversation with the agent
      await conversation.startSession(conversationOptions);
      
      setError(null);
    } catch (error: any) {
      // Set a user-friendly error message
      setError('We\'re having trouble connecting to our manifestation service. Please try again in a moment.');
      setIsInitializing(false);
    }
  }, [conversation, agentId, apiKey, selectedLanguage, user, dailyTalkTimeUsed, validateCredentials]);

  const stopConversation = useCallback(async () => {
    try {
      await conversation.endSession();
    } catch (error: any) {
      // Don't show technical error to user
    }
  }, [conversation]);

  // Format time in minutes and seconds
  const formatTime = (seconds: number): string => {
    if (seconds === Infinity) return 'Unlimited';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return {
    isConnected,
    isProcessing: conversation.status === 'connected',
    isSpeaking: conversation.isSpeaking,
    status: conversation.status,
    startConversation,
    stopConversation,
    userTranscript,
    aiTranscript,
    extractedManifestation,
    pendingManifestation,
    agreementDetected,
    error,
    isInitializing,
    // Talk time information
    dailyTalkTimeUsed,
    remainingTime,
    talkTimeWarning,
    formatTime
  };
};